import React from "react";
import { useLanguage } from "../../context/LanguageContext";
import { translations } from "../../i18n/translations";
import { motion } from "framer-motion";
import { FormValidationSummary, useFormValidation } from "../common/FormValidationSummary";
import type { FormStepProps } from "../../types/application";
import { getGroupedClassifications } from "../../data/jobClassifications";
import { isMobile } from "react-device-detect";

// Component for showing extracted data indicator
const ExtractedFieldIndicator: React.FC<{ isExtracted: boolean; t: any }> = ({
  isExtracted,
  t,
}) => {
  if (!isExtracted) return null;
  return (
    <div className="extracted-indicator">
      <svg fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clipRule="evenodd"
        />
      </svg>
      {t.extractedFromResume}
    </div>
  );
};

const RequiredQuestionText: React.FC<{ children: React.ReactNode; t: any }> = ({
  children,
  t,
}) => (
  <>
    <strong className="required text-red-700 mrgn-rght-sm">*</strong>
    <span
      className="field-name text-lg"
      style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
    >
      {children}
    </span>
    <strong
      className="required text-red-700 text-lg"
      style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
    >
      {" "}
      {t.required}
    </strong>
  </>
);

const EmploymentStatusStep: React.FC<FormStepProps> = ({
  data,
  onUpdate,
  onNext,
  onPrevious,
}) => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const validation = useFormValidation({ t });
  const handleEnhancedDataChange = (field: string, value: string) => {
    const enhancedData = { ...data.enhancedData, [field]: value };

    // If classification becomes Student, force level to 00
    if (field === "currentClassification" && value === "STDNT") {
      enhancedData.currentLevel = "00";
    }
    if (field === "GroupFullyAssessed" && value === "STDNT") {
      enhancedData.LevelFullyAssessed = "00";
    }
    if (field === "qualifiedFullyAssessedPool" && value !== "Yes") {
      enhancedData.staffingProcess = "";
      enhancedData.GroupFullyAssessed = "";
      enhancedData.LevelFullyAssessed = "";
    }
    onUpdate({ enhancedData });
  };

  const handleSubmit = (e: React.FormEvent) => {
    validation.handleSubmit(e, onNext);
  };

  const enhancedData = data.enhancedData || {};
  const extractedFields = data.extractionMetadata?.extractedFields || [];
  const isFieldExtracted = (fieldName: string) =>
    extractedFields.includes(fieldName);

  // Show additional fields if currently employed in FPS
  const isCurrentlyEmployedFPS = enhancedData.currentlyEmployedFPS === "Yes";
  const isQualifiedFullyAssessedPool =
    enhancedData.qualifiedFullyAssessedPool === "Yes";

  // Get job classifications data
  const groupedClassifications = getGroupedClassifications(language);

  if (isMobile)
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        className="wb-frmvld space-y-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Header */}
        <div className="text-left">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t.employmentHeader}
          </h2>
        </div>

        {/* Section: Employment Status */}
        <div className="space-y-8">
          {/* Currently Employed by Federal Public Service */}
          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                <RequiredQuestionText t={t}>
                  7. {t.currentlyEmployedFPSLabel}
                </RequiredQuestionText>
              </legend>
              <p className="text-lg text-gray-900 mb-3">
                {t.employmentDescription}
              </p>
              <ul className="list-unstyled lst-spcd-2">
                <li className="radio">
                  <input
                    type="radio"
                    name="currentlyEmployedFPS"
                    id="currentlyEmployedFPS-yes"
                    value="Yes"
                    checked={enhancedData.currentlyEmployedFPS === "Yes"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "currentlyEmployedFPS",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="currentlyEmployedFPS-yes">{t.currentlyEmployedFPSYes}</label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="currentlyEmployedFPS"
                    id="currentlyEmployedFPS-no"
                    value="No"
                    checked={enhancedData.currentlyEmployedFPS === "No"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "currentlyEmployedFPS",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="currentlyEmployedFPS-no">{t.currentlyEmployedFPSNo}</label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("currentlyEmployedFPS")}
              t={t}
            />
          </div>

          {/* Additional fields if currently employed in FPS */}
          {isCurrentlyEmployedFPS && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="space-y-6 border-t border-gray-200 pt-6"
              aria-live="polite"
              aria-atomic="true"
            >
              {/* Current Classification */}
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="currentClassification-question" className="question-legend">
                  <RequiredQuestionText t={t}>
                    8. {t.currentClassificationLabel}
                  </RequiredQuestionText>
                </legend>
                <p className="text-lg text-gray-900 mt-1">
                  {t.currentClassificationHelp}
                </p>
                <select
                  id="currentClassification"
                  name="currentClassification"
                  aria-labelledby="currentClassification-question"
                  value={enhancedData.currentClassification || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "currentClassification",
                      e.target.value
                    )
                  }
                  className="form-control"
                  required={isCurrentlyEmployedFPS}
                >
                  <option value="">{t.currentClassificationPlaceholder}</option>
                  {groupedClassifications.map((group) => (
                    <optgroup
                      key={group.groupCode}
                      label={`${group.groupCode} - ${group.groupName}`}
                    >
                      {group.subgroups.map((subgroup) => (
                        <option key={subgroup.code} value={subgroup.code}>
                          {subgroup.code} - {subgroup.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>

                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("currentClassification")}
                  t={t}
                />
              </fieldset>
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="currentLevel-question" className="question-legend">
                  <RequiredQuestionText t={t}>
                    8.5. {t.currentLevel}
                  </RequiredQuestionText>
                </legend>

                <select
                  id="currentLevel"
                  name="currentLevel"
                  aria-labelledby="currentLevel-question"
                  value={enhancedData.currentLevel || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("currentLevel", e.target.value)
                  }
                  className="form-control"
                  required={isCurrentlyEmployedFPS}
                  disabled={enhancedData.currentClassification === "STDNT"}
                >
                  <option value="">00</option>
                  <option value="01">01</option>
                  <option value="02">02</option>
                  <option value="03">03</option>
                  <option value="04">04</option>
                  <option value="05">05</option>
                  <option value="06">06</option>
                  <option value="07">07</option>
                  <option value="08">08</option>
                </select>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("currentLevel")}
                  t={t}
                />
              </fieldset>

              {/* Type of Employment */}
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="typeOfEmployment-question" className="question-legend">
                  <RequiredQuestionText t={t}>
                    9. {t.typeOfEmploymentLabel}
                  </RequiredQuestionText>
                </legend>
                <select
                  id="typeOfEmployment"
                  name="typeOfEmployment"
                  aria-labelledby="typeOfEmployment-question"
                  value={enhancedData.typeOfEmployment || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("typeOfEmployment", e.target.value)
                  }
                  className="form-control"
                  required={isCurrentlyEmployedFPS}
                >
                  <option value="">{t.typeOfEmploymentPlaceholder}</option>
                  <option value="Term">{t.typeOfEmploymentTerm}</option>
                  <option value="Indeterminate">
                    {t.typeOfEmploymentIndeterminate}
                  </option>
                  <option value="Student">{t.typeOfEmploymentStudent}</option>
                  <option value="Casual">{t.typeOfEmploymentCasual}</option>
                  <option value="Part-Time Worker">
                    {t.typeOfEmploymentPartTime}
                  </option>
                </select>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("typeOfEmployment")}
                  t={t}
                />
              </fieldset>

              {/* Employment End Date */}
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="employmentEndDate-question" className="question-legend">
                  <span
                    className="field-name text-lg"
                    style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
                  >
                    10. {t.employmentEndDateLabel}
                  </span>
                </legend>
                <p className="text-lg text-gray-900 mb-3">
                  {t.employmentEndDateHelp}
                </p>
                <input
                  type="date"
                  lang={language}
                  id="employmentEndDate"
                  name="employmentEndDate"
                  aria-labelledby="employmentEndDate-question"
                  max="9999-12-31"
                  value={enhancedData.employmentEndDate || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "employmentEndDate",
                      e.target.value
                    )
                  }
                  className="form-control"
                  // required={isCurrentlyEmployedFPS}
                />

                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("employmentEndDate")}
                  t={t}
                />
              </fieldset>

              {/* PRI Number */}
              <div>
                <fieldset className="gc-chckbxrdio">
                  <legend className="question-legend">
                    <RequiredQuestionText t={t}>
                      11. {t.priNumberLabel}
                    </RequiredQuestionText>
                  </legend>
                  <p className="text-lg text-gray-900 mb-3">{t.PRIDescription}</p>
                  <ul className="list-unstyled lst-spcd-2">
                    <li className="radio">
                      <input
                        type="radio"
                        name="priNumber"
                        id="priNumber-yes"
                        value="Yes"
                        checked={enhancedData.priNumber === "Yes"}
                        required={isCurrentlyEmployedFPS}
                        onChange={(e) =>
                          handleEnhancedDataChange("priNumber", e.target.value)
                        }
                      />
                      <label htmlFor="priNumber-yes">{t.priNumberYes}</label>
                    </li>
                    <li className="radio">
                      <input
                        type="radio"
                        name="priNumber"
                        id="priNumber-no"
                        value="No"
                        checked={enhancedData.priNumber === "No"}
                        onChange={(e) =>
                          handleEnhancedDataChange("priNumber", e.target.value)
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="priNumber-no">{t.priNumberNo}</label>
                    </li>
                    <li className="radio">
                      <input
                        type="radio"
                        name="priNumber"
                        id="priNumber-maybe"
                        value="Maybe"
                        checked={enhancedData.priNumber === "Maybe"}
                        onChange={(e) =>
                          handleEnhancedDataChange("priNumber", e.target.value)
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="priNumber-maybe">
                        {t.priNumberMaybe}
                      </label>
                    </li>
                  </ul>
                </fieldset>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("priNumber")}
                  t={t}
                />
              </div>

              {/* qualified in a fully assessed pool */}
              <div>
                <fieldset className="gc-chckbxrdio">
                  <legend className="question-legend">
                    <RequiredQuestionText t={t}>
                      12. {t.qualifiedFullyAssessedPoolLabel}
                    </RequiredQuestionText>
                  </legend>
                  <ul className="list-unstyled lst-spcd-2">
                    <li className="radio">
                      <input
                        type="radio"
                        name="qualifiedFullyAssessedPool"
                        id="qualifiedFullyAssessedPool-yes"
                        value="Yes"
                        checked={enhancedData.qualifiedFullyAssessedPool === "Yes"}
                        onChange={(e) =>
                          handleEnhancedDataChange(
                            "qualifiedFullyAssessedPool",
                            e.target.value
                          )
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="qualifiedFullyAssessedPool-yes">
                        {t.qualifiedFullyAssessedPoolYes}
                      </label>
                    </li>
                    <li className="radio">
                      <input
                        type="radio"
                        name="qualifiedFullyAssessedPool"
                        id="qualifiedFullyAssessedPool-no"
                        value="No"
                        checked={enhancedData.qualifiedFullyAssessedPool === "No"}
                        onChange={(e) =>
                          handleEnhancedDataChange(
                            "qualifiedFullyAssessedPool",
                            e.target.value
                          )
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="qualifiedFullyAssessedPool-no">
                        {t.qualifiedFullyAssessedPoolNo}
                      </label>
                    </li>
                  </ul>
                </fieldset>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("qualifiedFullyAssessedPool")}
                  t={t}
                />
              </div>

              {isQualifiedFullyAssessedPool && (
                <>
                  {/* Staffing Process */}
                  <fieldset className="space-y-2 border-0 p-0">
                    <legend id="staffingProcess-question" className="question-legend">
                      <RequiredQuestionText t={t}>
                        13. {t.staffingProcessLabel}
                      </RequiredQuestionText>
                    </legend>
                    <input
                      id="staffingProcess"
                      name="staffingProcess"
                      aria-labelledby="staffingProcess-question"
                      value={enhancedData.staffingProcess || ""}
                      onChange={(e) =>
                        handleEnhancedDataChange("staffingProcess", e.target.value)
                      }
                      className="form-control w-full"
                      placeholder={t.staffingProcessPlaceholder}
                      required={isQualifiedFullyAssessedPool}
                    />
                    <ExtractedFieldIndicator
                      isExtracted={isFieldExtracted("staffingProcess")}
                      t={t}
                    />
                  </fieldset>

                  {/* Group the user is fully assessed in */}
                  <fieldset className="space-y-2 border-0 p-0">
                    <legend id="GroupFullyAssessed-question" className="question-legend">
                      <RequiredQuestionText t={t}>
                        14. {t.GroupFullyAssessedLabel}:
                      </RequiredQuestionText>
                    </legend>
                    <select
                      id="GroupFullyAssessed"
                      name="GroupFullyAssessed"
                      aria-labelledby="GroupFullyAssessed-question"
                      value={enhancedData.GroupFullyAssessed || ""}
                      onChange={(e) =>
                        handleEnhancedDataChange(
                          "GroupFullyAssessed",
                          e.target.value
                        )
                      }
                      className="form-control"
                      required={isQualifiedFullyAssessedPool}
                    >
                      <option value="">{t.GroupFullyAssessedPlaceholder}</option>
                      {groupedClassifications.map((group) => (
                        <optgroup
                          key={group.groupCode}
                          label={`${group.groupCode} - ${group.groupName}`}
                        >
                          {group.subgroups.map((subgroup) => (
                            <option key={subgroup.code} value={subgroup.code}>
                              {subgroup.code} - {subgroup.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <ExtractedFieldIndicator
                      isExtracted={isFieldExtracted("GroupFullyAssessed")}
                      t={t}
                    />
                  </fieldset>

                  {/* level the user is fully assessed in */}
                  <fieldset className="space-y-2 border-0 p-0">
                    <legend id="LevelFullyAssessed-question" className="question-legend">
                      <RequiredQuestionText t={t}>
                        14.5. {t.LevelFullyAssessedLabel}:
                      </RequiredQuestionText>
                    </legend>

                    <select
                      id="LevelFullyAssessed"
                      name="LevelFullyAssessed"
                      aria-labelledby="LevelFullyAssessed-question"
                      value={enhancedData.LevelFullyAssessed || ""}
                      onChange={(e) =>
                        handleEnhancedDataChange("LevelFullyAssessed", e.target.value)
                      }
                      className="form-control"
                      required={isQualifiedFullyAssessedPool}
                      disabled={enhancedData.GroupFullyAssessed === "STDNT"}
                    >
                      <option value="">00</option>
                      <option value="01">01</option>
                      <option value="02">02</option>
                      <option value="03">03</option>
                      <option value="04">04</option>
                      <option value="05">05</option>
                      <option value="06">06</option>
                      <option value="07">07</option>
                      <option value="08">08</option>
                    </select>
                    <ExtractedFieldIndicator
                      isExtracted={isFieldExtracted("LevelFullyAssessed")}
                      t={t}
                    />
                  </fieldset>
                </>
              )}
            </motion.div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onPrevious}
            className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
          >
            {t.nextLanguageButton}
          </button>
        </div>
      </motion.form>
    );
  else
    return (
      <motion.form
        ref={validation.formRef}
        onSubmit={handleSubmit}
        onChangeCapture={validation.handleChangeCapture}
        className="wb-frmvld space-y-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        noValidate
      >
        <FormValidationSummary
          t={t}
          errors={validation.errors}
          errorSummaryRef={validation.errorSummaryRef}
          formRef={validation.formRef}
        />
        {/* Section: Employment Status */}
        <div className="space-y-8">
          {/* Currently Employed by Federal Public Service */}
          <div>
            <fieldset className="gc-chckbxrdio">
              <legend className="question-legend">
                <RequiredQuestionText t={t}>
                  7. {t.currentlyEmployedFPSLabel}
                </RequiredQuestionText>
              </legend>
              <p className="text-lg text-gray-700 mb-3">
                {t.employmentDescription}
              </p>
              <ul className="list-unstyled lst-spcd-2">
                <li className="radio">
                  <input
                    type="radio"
                    name="currentlyEmployedFPS"
                    id="currentlyEmployedFPS-yes-desktop"
                    value="Yes"
                    checked={enhancedData.currentlyEmployedFPS === "Yes"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "currentlyEmployedFPS",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="currentlyEmployedFPS-yes-desktop">{t.currentlyEmployedFPSYes}</label>
                </li>
                <li className="radio">
                  <input
                    type="radio"
                    name="currentlyEmployedFPS"
                    id="currentlyEmployedFPS-no-desktop"
                    value="No"
                    checked={enhancedData.currentlyEmployedFPS === "No"}
                    onChange={(e) =>
                      handleEnhancedDataChange(
                        "currentlyEmployedFPS",
                        e.target.value
                      )
                    }
                    required
                  />
                  <label htmlFor="currentlyEmployedFPS-no-desktop">{t.currentlyEmployedFPSNo}</label>
                </li>
              </ul>
            </fieldset>
            <ExtractedFieldIndicator
              isExtracted={isFieldExtracted("currentlyEmployedFPS")}
              t={t}
            />
          </div>

          {/* Additional fields if currently employed in FPS */}
          {isCurrentlyEmployedFPS && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="space-y-8 pt-4"
              aria-live="polite"
              aria-atomic="true"
            >
              {/* Current Classification */}
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="currentClassification-desktop-question" className="question-legend">
                  <RequiredQuestionText t={t}>
                    8. {t.currentClassificationLabel}
                  </RequiredQuestionText>
                </legend>
                <select
                  id="currentClassification"
                  name="currentClassification"
                  aria-labelledby="currentClassification-desktop-question"
                  value={enhancedData.currentClassification || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "currentClassification",
                      e.target.value
                    )
                  }
                  className="form-control"
                  required={isCurrentlyEmployedFPS}
                >
                  <option value="">{t.currentClassificationPlaceholder}</option>
                  {groupedClassifications.map((group) => (
                    <optgroup
                      key={group.groupCode}
                      label={`${group.groupCode} - ${group.groupName}`}
                    >
                      {group.subgroups.map((subgroup) => (
                        <option key={subgroup.code} value={subgroup.code}>
                          {subgroup.code} - {subgroup.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("currentClassification")}
                  t={t}
                />
              </fieldset>

              {/* Current Level */}
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="currentLevel-desktop-question" className="question-legend">
                  <RequiredQuestionText t={t}>
                    8.5. {t.currentLevel}
                  </RequiredQuestionText>
                </legend>
                <select
                  id="currentLevel"
                  name="currentLevel"
                  aria-labelledby="currentLevel-desktop-question"
                  value={enhancedData.currentLevel || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("currentLevel", e.target.value)
                  }
                  className="form-control"
                  required={isCurrentlyEmployedFPS}
                  disabled={enhancedData.currentClassification === "STDNT"}
                >
                  <option value="">00</option>
                  <option value="01">01</option>
                  <option value="02">02</option>
                  <option value="03">03</option>
                  <option value="04">04</option>
                  <option value="05">05</option>
                  <option value="06">06</option>
                  <option value="07">07</option>
                  <option value="08">08</option>
                </select>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("currentLevel")}
                  t={t}
                />
              </fieldset>

              {/* Type of Employment */}
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="typeOfEmployment-desktop-question" className="question-legend">
                  <RequiredQuestionText t={t}>
                    9. {t.typeOfEmploymentLabel}
                  </RequiredQuestionText>
                </legend>
                <select
                  id="typeOfEmployment"
                  name="typeOfEmployment"
                  aria-labelledby="typeOfEmployment-desktop-question"
                  value={enhancedData.typeOfEmployment || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange("typeOfEmployment", e.target.value)
                  }
                  className="form-control"
                  required={isCurrentlyEmployedFPS}
                >
                  <option value="">{t.typeOfEmploymentPlaceholder}</option>
                  <option value="Term">{t.typeOfEmploymentTerm}</option>
                  <option value="Indeterminate">
                    {t.typeOfEmploymentIndeterminate}
                  </option>
                  <option value="Student">{t.typeOfEmploymentStudent}</option>
                  <option value="Casual">{t.typeOfEmploymentCasual}</option>
                  <option value="Part-Time Worker">
                    {t.typeOfEmploymentPartTime}
                  </option>
                </select>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("typeOfEmployment")}
                  t={t}
                />
              </fieldset>

              {/* Employment End Date */}
              <fieldset className="space-y-2 border-0 p-0">
                <legend id="employmentEndDate-desktop-question" className="question-legend">
                  <span
                    className="field-name text-lg"
                    style={{ fontSize: "1.125rem", lineHeight: "1.75rem" }}
                  >
                    10. {t.employmentEndDateLabel}
                  </span>
                </legend>
                <p className="text-lg text-gray-950">{t.employmentDate}</p>
                <input
                  type="date"
                  lang={language}
                  id="employmentEndDate"
                  name="employmentEndDate"
                  aria-labelledby="employmentEndDate-desktop-question"
                  max="9999-12-31"
                  value={enhancedData.employmentEndDate || ""}
                  onChange={(e) =>
                    handleEnhancedDataChange(
                      "employmentEndDate",
                      e.target.value
                    )
                  }
                  className="form-control"
                  // required={isCurrentlyEmployedFPS}
                />
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("employmentEndDate")}
                  t={t}
                />
              </fieldset>

              {/* PRI Number */}
              <div className="space-y-4">
                <fieldset className="gc-chckbxrdio">
                  <legend className="question-legend">
                    <RequiredQuestionText t={t}>
                      11. {t.priNumberLabel}
                    </RequiredQuestionText>
                  </legend>
                  <p className="text-lg text-gray-700 mb-3">{t.PRIDescription}</p>
                  <ul className="list-unstyled lst-spcd-2">
                    <li className="radio">
                      <input
                        type="radio"
                        name="priNumber"
                        id="priNumber-yes-desktop"
                        value="Yes"
                        checked={enhancedData.priNumber === "Yes"}
                        onChange={(e) =>
                          handleEnhancedDataChange("priNumber", e.target.value)
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="priNumber-yes-desktop">{t.priNumberYes}</label>
                    </li>
                    <li className="radio">
                      <input
                        type="radio"
                        name="priNumber"
                        id="priNumber-no-desktop"
                        value="No"
                        checked={enhancedData.priNumber === "No"}
                        onChange={(e) =>
                          handleEnhancedDataChange("priNumber", e.target.value)
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="priNumber-no-desktop">{t.priNumberNo}</label>
                    </li>
                    <li className="radio">
                      <input
                        type="radio"
                        name="priNumber"
                        id="priNumber-maybe-desktop"
                        value="Maybe"
                        checked={enhancedData.priNumber === "Maybe"}
                        onChange={(e) =>
                          handleEnhancedDataChange("priNumber", e.target.value)
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="priNumber-maybe-desktop">{t.priNumberMaybe}</label>
                    </li>
                  </ul>
                </fieldset>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("priNumber")}
                  t={t}
                />
              </div>

              {/* qualified in a fully assessed pool */}
              <div className="space-y-4">
                <fieldset className="gc-chckbxrdio">
                  <legend className="question-legend">
                    <RequiredQuestionText t={t}>
                      12. {t.qualifiedFullyAssessedPoolLabel}
                    </RequiredQuestionText>
                  </legend>
                  <ul className="list-unstyled lst-spcd-2">
                    <li className="radio">
                      <input
                        type="radio"
                        name="qualifiedFullyAssessedPool"
                        id="qualifiedFullyAssessedPool-yes-desktop"
                        value="Yes"
                        checked={enhancedData.qualifiedFullyAssessedPool === "Yes"}
                        onChange={(e) =>
                          handleEnhancedDataChange("qualifiedFullyAssessedPool", e.target.value)
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="qualifiedFullyAssessedPool-yes-desktop">{t.qualifiedFullyAssessedPoolYes}</label>
                    </li>
                    <li className="radio">
                      <input
                        type="radio"
                        name="qualifiedFullyAssessedPool"
                        id="qualifiedFullyAssessedPool-no-desktop"
                        value="No"
                        checked={enhancedData.qualifiedFullyAssessedPool === "No"}
                        onChange={(e) =>
                          handleEnhancedDataChange("qualifiedFullyAssessedPool", e.target.value)
                        }
                        required={isCurrentlyEmployedFPS}
                      />
                      <label htmlFor="qualifiedFullyAssessedPool-no-desktop">{t.qualifiedFullyAssessedPoolNo}</label>
                    </li>
                  </ul>
                </fieldset>
                <ExtractedFieldIndicator
                  isExtracted={isFieldExtracted("qualifiedFullyAssessedPool")}
                  t={t}
                />
              </div>

              {isQualifiedFullyAssessedPool && (
                <>
                  {/* Staffing Process */}
                  <fieldset className="space-y-2 border-0 p-0">
                    <legend id="staffingProcess-desktop-question" className="question-legend">
                      <RequiredQuestionText t={t}>
                        13. {t.staffingProcessLabel}
                      </RequiredQuestionText>
                    </legend>
                    <input
                      id="staffingProcess"
                      name="staffingProcess"
                      aria-labelledby="staffingProcess-desktop-question"
                      value={enhancedData.staffingProcess || ""}
                      onChange={(e) =>
                        handleEnhancedDataChange("staffingProcess", e.target.value)
                      }
                      className="form-control w-full max-w-xs"
                      placeholder={t.staffingProcessPlaceholder}
                      required={isQualifiedFullyAssessedPool}
                    />
                    <ExtractedFieldIndicator
                      isExtracted={isFieldExtracted("staffingProcess")}
                      t={t}
                    />
                  </fieldset>

                  {/* Group the user is fully assessed in */}
                  <fieldset className="space-y-2 border-0 p-0">
                    <legend id="GroupFullyAssessed-desktop-question" className="question-legend">
                      <RequiredQuestionText t={t}>
                        14. {t.GroupFullyAssessedLabel}:
                      </RequiredQuestionText>
                    </legend>
                    <select
                      id="GroupFullyAssessed"
                      name="GroupFullyAssessed"
                      aria-labelledby="GroupFullyAssessed-desktop-question"
                      value={enhancedData.GroupFullyAssessed || ""}
                      onChange={(e) =>
                        handleEnhancedDataChange(
                          "GroupFullyAssessed",
                          e.target.value
                        )
                      }
                      className="form-control"
                      required={isQualifiedFullyAssessedPool}
                    >
                      <option value="">{t.GroupFullyAssessedPlaceholder}</option>
                      {groupedClassifications.map((group) => (
                        <optgroup
                          key={group.groupCode}
                          label={`${group.groupCode} - ${group.groupName}`}
                        >
                          {group.subgroups.map((subgroup) => (
                            <option key={subgroup.code} value={subgroup.code}>
                              {subgroup.code} - {subgroup.name}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    <ExtractedFieldIndicator
                      isExtracted={isFieldExtracted("GroupFullyAssessed")}
                      t={t}
                    />
                  </fieldset>

                  {/* level the user is fully assessed in */}
                  <fieldset className="space-y-2 border-0 p-0">
                    <legend id="currentLevel-desktop-question" className="question-legend">
                      <RequiredQuestionText t={t}>
                        14.5. {t.LevelFullyAssessedLabel}:
                      </RequiredQuestionText>
                    </legend>
                    <select
                      id="LevelFullyAssessed"
                      name="LevelFullyAssessed"
                      aria-labelledby="LevelFullyAssessed-desktop-question"
                      value={enhancedData.LevelFullyAssessed || ""}
                      onChange={(e) =>
                        handleEnhancedDataChange("LevelFullyAssessed", e.target.value)
                      }
                      className="form-control"
                      required={isQualifiedFullyAssessedPool}
                      disabled={enhancedData.GroupFullyAssessed === "STDNT"}
                    >
                      <option value="">00</option>
                      <option value="01">01</option>
                      <option value="02">02</option>
                      <option value="03">03</option>
                      <option value="04">04</option>
                      <option value="05">05</option>
                      <option value="06">06</option>
                      <option value="07">07</option>
                      <option value="08">08</option>
                    </select>
                    <ExtractedFieldIndicator
                      isExtracted={isFieldExtracted("LevelFullyAssessed")}
                      t={t}
                    />
                  </fieldset>
                </>
              )}
            </motion.div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between pt-6">
          <button
            type="button"
            onClick={onPrevious}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t.previousButton}
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-lg font-medium transition-colors primary-action-button"
          >
            {t.nextLanguageButton}
          </button>
        </div>
      </motion.form>
    );
};

export default EmploymentStatusStep;
